import h from 'hyperscript';

import type { Popup } from 'ui/popup/popup';
import { closePopup, openPopup } from 'ui/popup/popup';
import { createButton } from 'ui/button/button';

import { debounce } from 'cadenza/utils/debounce';
import type { EventKey } from 'cadenza/utils/event-util';
import { getTabbables, on, unByKey } from 'cadenza/utils/event-util';
import { uniqueId } from 'cadenza/utils/unique-id';
import { escapeForAttribute } from 'cadenza/utils/escape-html';
import { assertType } from 'cadenza/utils/custom-error';

import './autocomplete-input.css';

const FOCUSSED_ITEM_SELECTOR = '[aria-current=true]';

/** Dispatched when a suggestion is selected (via Enter key or click). */
export class SelectedEvent<T> extends CustomEvent<{ item: AutocompleteItem<T> }> {

  static get type () {
    return 'autocomplete:selected';
  }

  constructor (item: AutocompleteItem<T>) {
    super(SelectedEvent.type, { detail: { item } });
  }

}

/**
 * Dispatched when a suggestion is highlighted (via arrow keys or mouse hover) but not selected yet.
 */
export class FocussedEvent<T> extends CustomEvent<{ item?: AutocompleteItem<T> }> {

  static get type () {
    return 'autocomplete:focussed';
  }

  constructor (item?: AutocompleteItem<T>) {
    super(FocussedEvent.type, { detail: { item } });
  }

}

/** Dispatched after the dropdown with the suggestions has been opened. */
export class OpenedEvent extends CustomEvent<{ popup: Popup }> {

  static get type () {
    return 'autocomplete:opened';
  }

  constructor (popup: Popup) {
    super(OpenedEvent.type, { detail: { popup } });
  }

}

/** Dispatched after the dropdown with the suggestions has been closed. */
export class ClosedEvent extends CustomEvent<void> {

  static get type () {
    return 'autocomplete:closed';
  }

  constructor () {
    super(ClosedEvent.type);
  }

}

export interface AutocompleteItem<T> {
  id: string;
  label: string;
  category?: string;
  data: T;
}

/**
 * Optional settings for the Autocomplete class.
 */
export interface AutocompleteOptions {
  /**
   * Existing input field to use in the AutocompleteInput. If undefined, a new input field is
   * created.
   */
  input?: HTMLInputElement;
  /**
   * Target to attach the dropdown popup to. Defaults to the input element used.
   */
  popupTarget?: HTMLElement;

  /**
   * Wait time in ms for debouncing the contentSupplier.
   * If not specified, the DEFAULT_WAIT in debounce.ts is used.
   */
  debounceWait?: number;
  /**
   * Disabled state.
   */
  disabled?: boolean;
}

interface ItemElement<T> extends HTMLButtonElement {
  dAutocompleteItem: AutocompleteItem<T>;
}

export type AutocompleteContent<T> = AutocompleteItem<T>[];

const COMPONENT_NAME = 'd-autocomplete-input';

/**
 * An input field with autocomplete functionality
 */
export class AutocompleteInput<T> extends HTMLElement {

  #input: HTMLInputElement;
  #popupTarget: HTMLElement;
  #suggestionsSupplier;
  #dropdown?: Popup;
  #debouncedShowSuggestions;
  #dropdownId = uniqueId();
  #eventKeys: EventKey[] = [];

  /**
   * @param input - Existing input field to use in the AutocompleteInput. If undefined, a new input
   *     field is created.
   * @param suggestionsSupplier - callback that provides the suggestion list for the autocomplete
   *     dropdown based on a search term
   * @param options - optional settings
   */
  constructor (
    suggestionsSupplier: (searchTerm: string) => AutocompleteContent<T> | Promise<AutocompleteContent<T>>,
    { input, popupTarget, debounceWait, disabled = false }: AutocompleteOptions = {}
  ) {
    super();
    this.#input = input || h('input', { type: 'search' });
    this.#popupTarget = popupTarget ?? this.#input;
    this.#suggestionsSupplier = suggestionsSupplier;
    this.#debouncedShowSuggestions = debounce(this.#showSuggestions.bind(this), debounceWait);
    this.disabled = disabled;
  }

  connectedCallback () {
    this.#input.classList.add('form-control');
    this.#input.setAttribute('aria-autocomplete', 'list');
    this.#input.setAttribute('aria-haspopup', 'menu');

    this.#eventKeys.push(
      on(this.#input, 'input', this.#debouncedShowSuggestions),
      on<KeyboardEvent>(this.#input, 'keydown', e => {
        if (!this.#dropdown) {
          return;
        }

        switch (e.key) {
          case 'ArrowDown':
            this.#focusNext(this.#dropdown);
            break;
          case 'ArrowUp':
            this.#focusPrevious(this.#dropdown);
            break;
          case 'Enter':
            this.#focussedItemElement?.click();
            e.preventDefault();
            break;
          case 'Escape':
          case 'Tab':
            closePopup(this.#popupTarget);
            break;
        }
      })
    );

    this.append(this.#input);
  }

  disconnectedCallback () {
    unByKey(this.#eventKeys);
  }

  focusById (id: string) {
    const itemElement = this.#getElementById(id);
    if (itemElement) {
      this.#focusItem(itemElement);
    }
  }

  selectById (id: string) {
    if (!this.#dropdown) {
      return;
    }
    const itemToSelect = this.#getElementById(id);
    if (itemToSelect) {
      this.#selectItem(itemToSelect);
    }
  }

  refreshSuggestions () {
    this.#debouncedShowSuggestions();
  }

  get value () {
    return this.#input.value || '';
  }

  set value (value) {
    this.#input.value = value;
  }

  async #showSuggestions () {
    const suggestionsOrPromise = this.#suggestionsSupplier(this.#input.value);
    if (suggestionsOrPromise instanceof Promise) {
      const resolvedSuggestions = await suggestionsOrPromise;
      this.#showResolvedSuggestions(resolvedSuggestions);
    } else {
      this.#showResolvedSuggestions(suggestionsOrPromise);
    }
  }

  #showResolvedSuggestions (autocompleteContent: AutocompleteContent<T>) {
    if (!this.#dropdown) {
      this.#dropdown = this.#createDropdown();
      this.#openDropdown(this.#dropdown);
    } else {
      this.#dropdown.innerHTML = '';
    }
    this.#dropdown.append(...this.#createDropdownContent(autocompleteContent));
  }

  #createDropdown () {
    return h<Popup>(`.d-menu.d-autocomplete-input--dropdown#${this.#dropdownId}`, {
      attrs: { role: 'menu' }
    });
  }

  #openDropdown (dropdown: Popup) {
    // We don't use Menu but the more basic Popup component because the focus handling is different
    openPopup(this.#popupTarget, dropdown, {
      placement: 'bottom-start',
      hasSameWidthAsTarget: true,
      onOpen: () => this.#input.focus(),
      onClose: () => {
        this.#dropdown = undefined;
        this.dispatchEvent(new ClosedEvent());
      }
    });
    this.dispatchEvent(new OpenedEvent(dropdown));
  }

  #createDropdownContent (autocompleteContent: AutocompleteContent<T>) {
    const dropdownContent: HTMLElement[] = [];
    let lastCategory: string | undefined;
    let currentCategoryDiv: HTMLDivElement | undefined;
    autocompleteContent.forEach((autocompleteContentItem) => {
      if (autocompleteContentItem.category && autocompleteContentItem.category !== lastCategory) {
        currentCategoryDiv = h<HTMLDivElement>('div', { attrs: { role: 'group' } });
        dropdownContent.push(currentCategoryDiv);
        currentCategoryDiv.append(h('label', autocompleteContentItem.category));
        lastCategory = autocompleteContentItem.category;
      }

      const itemElement: HTMLElement = createButton(autocompleteContentItem.label, {
        id: this.#itemId(autocompleteContentItem.id),
        dAutocompleteItem: autocompleteContentItem,
        attrs: { role: 'menuitem' },
        onclick: () => { this.#selectItem(itemElement); },
        onmouseenter: () => this.#focusItem(itemElement)
      });

      if (autocompleteContentItem.category) {
        // if the item has a category, we know that currentCategoryDiv must exist
        currentCategoryDiv!.append(itemElement);
      } else {
        dropdownContent.push(itemElement);
      }
    });
    const dropDownMenuItems = dropdownContent.filter(contentElem => contentElem.role === 'menuitem');
    if (dropDownMenuItems.length > 0) {
      this.#focusItem(dropDownMenuItems[0]);
    }
    return dropdownContent;
  }

  #selectItem (item: HTMLElement) {
    assertType<ItemElement<T>>(item, 'dAutocompleteItem' in item);
    this.#input.value = item.dAutocompleteItem.label;
    this.dispatchEvent(new SelectedEvent(item.dAutocompleteItem));
    closePopup(this.#popupTarget);
  }

  #focusNext (menu: Popup) {
    const tabbables = getTabbables(menu);
    if (tabbables.length === 0) {
      return;
    }
    const selectedIndex = getSelectedIndex(tabbables);
    if (selectedIndex < tabbables.length - 1) {
      this.#focusItem(tabbables[selectedIndex + 1]);
    } else {
      this.#focusFirst(tabbables);
    }
  }

  #focusPrevious (menu: Popup) {
    const tabbables = getTabbables(menu);
    if (tabbables.length === 0) {
      return;
    }
    const selectedIndex = getSelectedIndex(tabbables);
    if (selectedIndex > 0) {
      this.#focusItem(tabbables[selectedIndex - 1]);
    } else {
      this.#focusLast(tabbables);
    }
  }

  #focusFirst (tabbables: HTMLElement[] | HTMLElement) {
    if (!Array.isArray(tabbables)) {
      tabbables = getTabbables(tabbables);
    }
    if (tabbables.length > 0) {
      this.#focusItem(tabbables[0]);
    }
  }

  #focusLast (tabbables: HTMLElement[] | HTMLElement) {
    if (!Array.isArray(tabbables)) {
      tabbables = getTabbables(tabbables);
    }
    if (tabbables.length > 0) {
      this.#focusItem(tabbables[tabbables.length - 1]);
    }
  }

  #focusItem (element: HTMLElement) {
    assertType<ItemElement<T>>(element, 'dAutocompleteItem' in element);
    if (!this.#dropdown || getTabbables(this.#dropdown).length === 0) {
      return;
    }
    this.#focussedItemElement?.removeAttribute('aria-current');
    element.setAttribute('aria-current', 'true');
    this.#dropdown.setAttribute('aria-activedescendant', element.id);
    this.dispatchEvent(new FocussedEvent<T>(element.dAutocompleteItem));
  }

  #getElementById (id: string) {
    return this.#dropdown?.querySelector<ItemElement<T>>(`[id="${this.#itemId(id)}"]`);
  }

  get #focussedItemElement () {
    return this.#dropdown?.querySelector<ItemElement<T>>(FOCUSSED_ITEM_SELECTOR);
  }

  #itemId (idWithoutPrefix: string) {
    return `${this.#dropdownId}-${escapeForAttribute(idWithoutPrefix)}`;
  }

  get disabled () {
    return this.hasAttribute('disabled');
  }

  set disabled (value) {
    this.#input.disabled = value;
    this.toggleAttribute('disabled', value);
  }

}

customElements.define(COMPONENT_NAME, AutocompleteInput);

function getSelectedIndex (tabbables: HTMLElement[]) {
  return tabbables.findIndex(tabbable => tabbable.matches(FOCUSSED_ITEM_SELECTOR));
}
